import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import ConfirmButton from "@/components/ConfirmButton";
import GrantAccessModal from "@/components/admin/GrantAccessModal";
import { AVAILABLE_TOOLS } from "@/lib/tools";

const ROLES = [
  { value: "admin",   label: "Administrador", desc: "Acesso total ao sistema." },
  { value: "manager", label: "Gestor (Sollute)", desc: "Cadastra empresas, conduz avaliações." },
  { value: "client",  label: "Cliente (empresa)", desc: "Acessa apenas suas empresas atribuídas." },
];

const ROLE_BADGE = {
  admin:   "bg-purple-100 text-purple-900 border-purple-300",
  manager: "bg-emerald-100 text-emerald-900 border-emerald-300",
  client:  "bg-sky-100 text-sky-900 border-sky-300",
};

export default function Users() {
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(null);
  const [grantTarget, setGrantTarget] = useState(null);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "manager", client_company_ids: [], company_name: "", send_invite: true, tools: [] });

  const load = useCallback(async () => {
    const [u, c] = await Promise.all([api.get("/admin/users"), api.get("/companies")]);
    setUsers(u.data); setCompanies(c.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm({ email: "", password: "", name: "", role: "manager", client_company_ids: [], company_name: "", send_invite: true, tools: [] });
    setShow(true);
  };
  const openEdit = (u) => {
    setEditing(u);
    setForm({
      email: u.email, password: "", name: u.name, role: u.role,
      client_company_ids: u.client_company_ids || [], company_name: u.company_name || "",
      send_invite: false,
      tools: u.tools || [],
    });
    setShow(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/admin/users/${editing.id}`, {
          name: form.name, role: form.role,
          client_company_ids: form.client_company_ids,
          company_name: form.company_name,
          tools: form.tools,
        });
        if (form.password) {
          await api.post(`/admin/users/${editing.id}/password`, { password: form.password });
        }
        toast.success("Usuário atualizado");
      } else {
        const payload = {
          email: form.email, name: form.name, role: form.role,
          client_company_ids: form.client_company_ids, company_name: form.company_name,
          send_invite: form.send_invite,
        };
        if (!form.send_invite && form.password) payload.password = form.password;
        const { data } = await api.post("/admin/users", payload);
        if (form.send_invite) {
          toast.success(data.invite_sent ? "Convite enviado por e-mail" : "Usuário criado, mas o e-mail não pôde ser entregue. Verifique o destinatário (em modo teste só envia para o e-mail verificado da conta Resend).");
        } else {
          toast.success("Usuário criado");
        }
      }
      setShow(false); load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
  };

  const resendInvite = async (u) => {
    try {
      const { data } = await api.post(`/admin/users/${u.id}/invite`);
      if (data.delivered) {
        toast.success(`Convite reenviado para ${u.email}`);
      } else {
        toast.warning(data.detail || `Convite gerado, mas e-mail não entregue para ${u.email}.`);
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    }
  };

  const toggleActive = async (u) => {
    await api.put(`/admin/users/${u.id}`, { active: !u.active });
    toast.success(u.active ? "Usuário desativado" : "Usuário reativado");
    load();
  };

  const del = async (id) => {
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("Usuário excluído"); load();
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };

  const toggleCompany = (cid) => {
    setForm((p) => ({
      ...p,
      client_company_ids: p.client_company_ids.includes(cid)
        ? p.client_company_ids.filter((x) => x !== cid)
        : [...p.client_company_ids, cid],
    }));
  };

  return (
    <div className="p-8 lg:p-12 max-w-7xl mx-auto">
      <div className="flex justify-between items-end mb-10">
        <div>
          <div className="overline mb-2">Administração</div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight text-slate-900">Usuários</h1>
          <p className="text-slate-600 mt-2">Gerencie acessos de gestores Sollute, administradores e clientes.</p>
        </div>
        <button onClick={openNew} className="btn btn-primary" data-testid="user-new-button">
          <i className="fa-solid fa-user-plus"></i> Novo usuário
        </button>
      </div>

      <div className="card">
        <table className="w-full text-sm">
          <thead className="text-left text-slate-500 border-b border-slate-200">
            <tr>
              <th className="py-3 font-semibold uppercase text-xs">Nome / E-mail</th>
              <th className="font-semibold uppercase text-xs">Papel</th>
              <th className="font-semibold uppercase text-xs">Empresas (cliente)</th>
              <th className="font-semibold uppercase text-xs">Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`user-row-${u.id}`}>
                <td className="py-3">
                  <div className="font-semibold text-slate-900">{u.name}</div>
                  <div className="text-xs text-slate-500 font-mono">{u.email}</div>
                </td>
                <td>
                  <span className={`${ROLE_BADGE[u.role] || ""} border px-2 py-1 text-[10px] font-bold uppercase tracking-wider`}>
                    {ROLES.find((r) => r.value === u.role)?.label || u.role}
                  </span>
                </td>
                <td className="text-xs text-slate-600">
                  {u.role === "client"
                    ? (u.client_company_ids?.length || 0) + " empresa(s)"
                    : "—"}
                </td>
                <td>
                  <div className="flex items-center gap-2">
                    <button onClick={() => toggleActive(u)} data-testid={`user-toggle-${u.id}`}
                            className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 border ${u.active === false ? "risk-0" : "status-concluido"}`}>
                      {u.active === false ? "Desativado" : "Ativo"}
                    </button>
                    {u.invite_pending && (
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 border border-amber-300 bg-amber-50 text-amber-900"
                            data-testid={`user-invite-pending-${u.id}`}>
                        <i className="fa-solid fa-clock mr-1"></i>Convite pendente
                      </span>
                    )}
                  </div>
                </td>
                <td className="text-right space-x-2 whitespace-nowrap">
                  <button onClick={() => setGrantTarget(u)} className="btn btn-ghost text-xs text-emerald-700 hover:text-emerald-900" data-testid={`user-grant-${u.id}`}
                          title="Liberar plano manualmente (cliente Comercial)">
                    <i className="fa-solid fa-key"></i>
                  </button>
                  <button onClick={() => resendInvite(u)} className="btn btn-ghost text-xs" data-testid={`user-resend-${u.id}`}
                          title="Reenviar convite / link de senha">
                    <i className="fa-solid fa-paper-plane"></i>
                  </button>
                  <button onClick={() => openEdit(u)} className="btn btn-ghost text-xs" data-testid={`user-edit-${u.id}`}>
                    <i className="fa-solid fa-pen"></i>
                  </button>
                  <ConfirmButton onConfirm={() => del(u.id)} testid={`user-delete-${u.id}`} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {show && (
        <div className="fixed inset-0 bg-slate-900/60 z-40 flex items-center justify-center p-4" onClick={() => setShow(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={save}
                className="bg-white border w-full max-w-2xl p-8 max-h-[90vh] overflow-y-auto" data-testid="user-modal">
            <div className="overline mb-2">{editing ? "Editar" : "Novo"} usuário</div>
            <h2 className="font-display text-2xl font-extrabold mb-6 tracking-tight">
              {editing ? "Editar acesso" : "Criar acesso"}
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="label">Nome completo *</label>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                       className="input" data-testid="user-field-name" />
              </div>
              <div>
                <label className="label">E-mail *</label>
                <input required type="email" disabled={!!editing}
                       value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                       className="input" data-testid="user-field-email" />
              </div>
              <div className="md:col-span-2">
                {!editing && (
                  <div className="mb-4 p-3 border border-emerald-200 bg-emerald-50">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input type="checkbox" checked={form.send_invite}
                             onChange={(e) => setForm({ ...form, send_invite: e.target.checked })}
                             className="mt-1 h-4 w-4 accent-emerald-900"
                             data-testid="user-send-invite" />
                      <span className="text-sm text-slate-800">
                        <strong>Enviar convite por e-mail</strong>
                        <div className="text-xs text-slate-600 mt-1">
                          O usuário receberá um link para criar a própria senha (válido por 72h).
                          Recomendado.
                        </div>
                      </span>
                    </label>
                  </div>
                )}
                {(!form.send_invite || editing) && (
                  <>
                    <label className="label">{editing ? "Nova senha (deixe vazio para manter)" : "Senha *"}</label>
                    <input type="password" minLength={editing ? 0 : 6} required={!editing && !form.send_invite}
                           value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                           className="input" data-testid="user-field-password" />
                  </>
                )}
              </div>
              <div className="md:col-span-2">
                <label className="label">Papel</label>
                <div className="grid md:grid-cols-3 gap-2">
                  {ROLES.map((r) => (
                    <button type="button" key={r.value}
                            onClick={() => setForm({ ...form, role: r.value })}
                            data-testid={`user-role-${r.value}`}
                            className={`text-left p-3 border transition-all ${
                              form.role === r.value ? "border-sollute-green bg-emerald-50" : "border-slate-200 hover:border-slate-400 bg-white"
                            }`}>
                      <div className="font-bold text-slate-900 text-sm">{r.label}</div>
                      <div className="text-[10px] text-slate-500 mt-1">{r.desc}</div>
                    </button>
                  ))}
                </div>
              </div>
              {form.role === "client" && (
                <div className="md:col-span-2">
                  <label className="label">Empresas que este cliente pode acessar</label>
                  <div className="border border-slate-200 max-h-48 overflow-y-auto p-2 space-y-1">
                    {companies.length === 0 && <div className="text-xs text-slate-500 p-2">Nenhuma empresa cadastrada.</div>}
                    {companies.map((c) => (
                      <label key={c.id} className="flex items-center gap-2 p-2 hover:bg-slate-50 cursor-pointer text-sm">
                        <input type="checkbox" checked={form.client_company_ids.includes(c.id)}
                               onChange={() => toggleCompany(c.id)}
                               data-testid={`user-company-${c.id}`} />
                        <span>{c.razao_social}</span>
                        {c.nome_fantasia && <span className="text-xs text-slate-500">· {c.nome_fantasia}</span>}
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <div className="md:col-span-2">
                <label className="label">Empresa de origem (opcional)</label>
                <input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                       className="input" data-testid="user-field-company" />
              </div>
              {editing && (
                <div className="md:col-span-2">
                  <label className="label">
                    Ferramentas extras liberadas
                    <span className="text-[10px] font-normal text-slate-500 ml-2">
                      (não-administradores só acessam o que estiver marcado)
                    </span>
                  </label>
                  <div className="space-y-2">
                    {AVAILABLE_TOOLS.map((t) => {
                      const checked = (form.tools || []).includes(t.id);
                      return (
                        <label
                          key={t.id}
                          className={`flex items-start gap-3 p-3 border cursor-pointer transition-colors ${
                            checked ? "border-emerald-700 bg-emerald-50" : "border-slate-200 hover:border-slate-400"
                          }`}
                          data-testid={`user-tool-${t.id}`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => {
                              setForm((p) => ({
                                ...p,
                                tools: checked
                                  ? p.tools.filter((x) => x !== t.id)
                                  : [...(p.tools || []), t.id],
                              }));
                            }}
                            className="mt-1 h-4 w-4 accent-emerald-900"
                          />
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <i className={`fa-solid ${t.icon} text-emerald-700`}></i>
                              <span className="font-semibold text-slate-900 text-sm">{t.label}</span>
                            </div>
                            <div className="text-xs text-slate-600 mt-1 leading-relaxed">{t.description}</div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button type="button" onClick={() => setShow(false)} className="btn btn-secondary" data-testid="user-modal-cancel">Cancelar</button>
              <button type="submit" className="btn btn-primary" data-testid="user-modal-save">
                <i className="fa-solid fa-floppy-disk"></i> Salvar
              </button>
            </div>
          </form>
        </div>
      )}

      {grantTarget && (
        <GrantAccessModal
          user={grantTarget}
          onClose={() => setGrantTarget(null)}
          onSuccess={load}
        />
      )}
    </div>
  );
}
